import { useState } from 'react';

import {
  EmailDraft,
  Flow,
  Layout,
  Settings,
  currencySymbolFor,
} from './types';
import { SampleProduct } from './api';
import { buttonTextForFlow, PRODUCT_IMAGE_PLACEHOLDER_BG } from './generateEmailHtml';

type Viewport = 'desktop' | 'mobile';

const FRAME_WIDTH: Record<Viewport, number> = {
  desktop: 600,
  mobile: 375,
};

// Mirrors the price formatting in generateEmailHtml (currency symbol +
// two decimals) so the preview matches the email Klaviyo will render.
function formatPrice(price: number | null, currencyCode: string): string | null {
  if (price === null) {
    return null;
  }

  return `${currencySymbolFor(currencyCode)}${price.toFixed(2)}`;
}

function ProductInfo({
  p, textColor, currencyCode,
}: { p: SampleProduct; textColor: string; currencyCode: string }) {
  const nameStyle: React.CSSProperties = {
    margin: '6px 0 2px', fontWeight: 600, fontSize: 13, color: textColor,
  };
  const priceStyle: React.CSSProperties = { margin: 0, fontSize: 12, color: textColor };
  const priceText = formatPrice(p.price, currencyCode);

  return (
    <>
      <p style={nameStyle}>{p.name}</p>
      {priceText !== null && <p style={priceStyle}>{priceText}</p>}
    </>
  );
}

type ProductsPreviewProps = {
  products: SampleProduct[];
  layout: Layout;
  settings: Settings;
  viewport: Viewport;
};

function ProductsPreview({
  products, layout, settings, viewport,
}: ProductsPreviewProps) {
  const { textColor, currencyCode } = settings;
  const imgHeight = viewport === 'mobile'
    ? settings.productImageMaxHeightMobile
    : settings.productImageMaxHeightDesktop;

  const imgStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    aspectRatio: '1 / 1',
    maxHeight: imgHeight,
    objectFit: 'cover',
    border: 0,
    background: PRODUCT_IMAGE_PLACEHOLDER_BG,
  };

  const nameStyle: React.CSSProperties = {
    margin: '6px 0 2px', fontWeight: 600, fontSize: 13, color: textColor,
  };
  const priceStyle: React.CSSProperties = { margin: 0, fontSize: 12, color: textColor };

  if (layout === 'grid') {
    return (
      <div style={{ display: 'flex', gap: 8, padding: '8px 0' }}>
        {products.slice(0, 3).map((p, i) => (
          <div key={p.handle || i} style={{ flex: 1, minWidth: 0 }}>
            <img src={p.imageSrc} alt={p.imageAlt} style={imgStyle} />
            <ProductInfo p={p} textColor={textColor} currencyCode={currencyCode} />
          </div>
        ))}
      </div>
    );
  }

  if (layout === 'hero-supporting') {
    const [hero, ...supporting] = products;
    const heroPrice = hero ? formatPrice(hero.price, currencyCode) : null;
    return (
      <div style={{ padding: '8px 0' }}>
        {hero && (
          <div style={{ marginBottom: 12 }}>
            <img src={hero.imageSrc} alt={hero.imageAlt} style={imgStyle} />
            <p style={{ ...nameStyle, fontSize: 15 }}>{hero.name}</p>
            {heroPrice !== null && <p style={{ ...priceStyle, margin: '0 0 8px' }}>{heroPrice}</p>}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          {supporting.slice(0, 2).map((p, i) => (
            <div key={p.handle || i} style={{ flex: 1, minWidth: 0 }}>
              <img src={p.imageSrc} alt={p.imageAlt} style={imgStyle} />
              <ProductInfo p={p} textColor={textColor} currencyCode={currencyCode} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (layout === 'horizontal-card') {
    return (
      <div style={{ padding: '8px 0' }}>
        {products.slice(0, 3).map((p, i) => (
          <div key={p.handle || i} style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <img
              src={p.imageSrc}
              alt={p.imageAlt}
              style={{
                width: 80,
                height: 80,
                objectFit: 'cover',
                border: 0,
                flexShrink: 0,
                background: PRODUCT_IMAGE_PLACEHOLDER_BG,
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <ProductInfo p={p} textColor={textColor} currencyCode={currencyCode} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 0' }}>
      {products.slice(0, 3).map((p) => (
        <div key={p.handle} style={{ marginBottom: 12 }}>
          <img src={p.imageSrc} alt={p.imageAlt} style={imgStyle} />
          <ProductInfo p={p} textColor={textColor} currencyCode={currencyCode} />
        </div>
      ))}
    </div>
  );
}

type EmailPreviewProps = {
  flow: Flow;
  draft: EmailDraft | null;
  layout: Layout;
  settings: Settings;
  sampleProducts: SampleProduct[];
};

function EmailPreview({
  flow, draft, layout, settings, sampleProducts,
}: EmailPreviewProps) {
  const [viewport, setViewport] = useState<Viewport>('desktop');
  const products = sampleProducts;

  const ctaText = buttonTextForFlow(flow, settings) || 'Shop now';

  return (
    <div className="eg-preview-panel">
      <div className="eg-preview-toolbar">
        <div className="eg-flow-tabs">
          <button
            type="button"
            className={`eg-tab${viewport === 'desktop' ? ' eg-tab-active' : ''}`}
            onClick={() => setViewport('desktop')}
          >
            Desktop
          </button>
          <button
            type="button"
            className={`eg-tab${viewport === 'mobile' ? ' eg-tab-active' : ''}`}
            onClick={() => setViewport('mobile')}
          >
            Mobile
          </button>
        </div>
      </div>
      <div className="eg-preview-scroll">
        <div className="eg-email-frame" style={{ width: FRAME_WIDTH[viewport] }}>
          <div
            className="eg-email-body"
            style={{ backgroundColor: settings.backgroundColor, color: settings.textColor }}
          >
            {draft?.headline ? (
              <h2 className="eg-email-headline">{draft.headline}</h2>
            ) : (
              <h2 className="eg-email-headline eg-email-placeholder">Headline</h2>
            )}
            {draft?.body ? (
              <p className="eg-email-copy">{draft.body}</p>
            ) : (
              <p className="eg-email-placeholder">Body copy will appear here.</p>
            )}
            {products.length > 0 ? (
              <ProductsPreview
                products={products}
                layout={layout}
                settings={settings}
                viewport={viewport}
              />
            ) : (
              <div className="eg-email-products-placeholder">
                No products found
              </div>
            )}
            <div className="eg-email-cta">
              <span
                className="eg-email-button"
                style={{
                  backgroundColor: settings.primaryColor,
                  color: settings.buttonTextColor,
                  borderRadius: settings.buttonBorderRadius,
                }}
              >
                {ctaText}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailPreview;
